import {
	Box,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Grid,
} from '@mui/material'
import {
	GridColDef,
	GridRowSelectionModel,
	useGridApiRef,
} from '@mui/x-data-grid-premium'
import {
	GridContainer,
	PageContainer,
	PageTop,
	SearchContainer,
	StyledDataGrid,
} from 'components/ApplicationStyles'
import PagePosition from 'components/PagePosition'
import { useEffect, useMemo, useState } from 'react'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { alertModal, confirmAlert, timerAlert } from '../../../util/util'
import { koKR } from '@mui/x-data-grid-premium/locales'
import { SpRequestType } from 'constants/types'
import { apiSurveyMasterList, apiSurveyAnswersBySurveyId } from 'api/api'
import SurveyPopup from './SurveyPopup'
import {
	SurveyItemType,
	SurveyMasterSearchType,
	SurveyMasterType,
	SurveyAnswerSummaryType,
} from 'constants/appType'
import SurveyAnswersPopup from './SurveyAnswersPopup'

const SurveyResult = () => {
	const apiRef = useGridApiRef()
	const [rowSelectionModel, setRowSelectionModel] =
		useState<GridRowSelectionModel>({ type: 'include', ids: new Set() })

	const disabledAdd = true
	const disabledDel = true
	const disabledSave = true
	const disabledSearch = false
	const disabledReset = false
	const disabledExcelDownload = false
	const hideAdd = true
	const hideDel = true
	const hideSave = true
	const hideSearch = true
	const hideReset = true
	const hideExcelDownload = true
	const [loading, setLoading] = useState(false)

	const [popupIsOpen, setPopupIsOpen] = useState(false)

	const [selectedData, setSelectedData] = useState<SurveyMasterType | null>(null)

	const [surveyAnswers, setSurveyAnswers] = useState<SurveyAnswerSummaryType[]>(
		[],
	)

	const [rows, setRows] = useState<SurveyMasterType[]>([])
	const gridColumns: GridColDef[] = useMemo(
		() => [
			{
				field: 'id',
				headerName: 'ID',
				width: 100,
				editable: false,
				type: 'number',
			},
			{
				field: 'title',
				headerName: '설문 제목',
				minWidth: 300,
				flex: 1,
				renderCell: (params) => {
					const { id, title, startDate, endDate, isActive } = params.row

					if (id === 0) {
						return <>{title}</>
					}
					return (
						<>
							<button
								type="button"
								style={{
									background: 'none',
									border: 'none',
									color: '#1976d2',
									textDecoration: 'underline',
									cursor: 'pointer',
								}}
								onClick={() => surveyPopupOpenAction(params.row)}
							>
								{title}
							</button>
							{/* startDate, endDate, isActive 기준으로 상태 표기 */}
							{dayjs().isBefore(dayjs(startDate)) ? (
								<span style={{ color: '#ff9800', marginLeft: 8 }}>
									{isActive ? '(예정)' : '(비활성)'}
								</span>
							) : dayjs().isAfter(dayjs(endDate)) ? (
								<span style={{ color: '#9e9e9e', marginLeft: 8 }}>
									{isActive ? '(종료)' : '(비활성)'}
								</span>
							) : (
								<span style={{ color: '#4caf50', marginLeft: 8 }}>
									{isActive ? '(진행중)' : '(비활성)'}
								</span>
							)}
						</>
					)
				},
			},
			{
				field: 'isActive',
				headerName: '활성 여부',
				width: 150,
				type: 'boolean',
			},
			{
				field: 'startDate',
				headerName: '시작일',
				width: 200,
				type: 'dateTime',
				valueFormatter: (value: any) => {
					if (!value) return ''
					return dayjs(value).format('YYYY-MM-DD HH:mm')
				},
				valueGetter: (value: any) => {
					if (!value) return null
					// 문자열인 경우 Date 객체로 변환
					if (typeof value === 'string') {
						return new Date(value)
					}
					// 이미 Date 객체인 경우 그대로 반환
					return value instanceof Date ? value : new Date(value)
				},
			},
			{
				field: 'endDate',
				headerName: '종료일',
				width: 200,
				type: 'dateTime',
				valueFormatter: (value: any) => {
					if (!value) return ''
					return dayjs(value).format('YYYY-MM-DD HH:mm')
				},
				valueGetter: (value: any) => {
					if (!value) return null
					// 문자열인 경우 Date 객체로 변환
					if (typeof value === 'string') {
						return new Date(value)
					}
					// 이미 Date 객체인 경우 그대로 반환
					return value instanceof Date ? value : new Date(value)
				},
			},
			{
				field: 'regId',
				headerName: '등록자',
				width: 150,
				editable: false,
			},
			{
				field: 'createdAt',
				headerName: '등록일',
				width: 200,
				editable: false,
				renderCell: (params) => {
					// createdAt보다 updatedAt값의 날짜 차이를 계산해서 upatedAt이 크면 updatedAt을 표시한다 또한 날짜가 오늘이면 시분초만 표기하고 아니면 년월일 시분초 표기
					const createdAt = dayjs(params.row.createdAt)
					const updatedAt = dayjs(params.row.updatedAt)
					const isToday = updatedAt.isSame(dayjs(), 'day')

					if (updatedAt.isAfter(createdAt)) {
						return isToday
							? updatedAt.format('HH:mm:ss')
							: updatedAt.format('YYYY-MM-DD HH:mm:ss')
					} else {
						return isToday
							? createdAt.format('HH:mm:ss')
							: createdAt.format('YYYY-MM-DD HH:mm:ss')
					}
				},
			},
		],
		[rows],
	)

	// 설문 팝업 열기 액션
	const surveyPopupOpenAction = (data: SurveyMasterType) => {
		setSelectedData(data)
		if (data.id) {
			apiSurveyAnswersBySurveyId(data.id).then((res) => {
				console.log('설문 답변 조회:', res)
				setSurveyAnswers(res)
				setSelectedData(data)
				setPopupIsOpen(true)
			})
		} else {
			setSurveyAnswers([])
			setSelectedData(null)
			setPopupIsOpen(false)
		}
	}

	const [search, setSearch] = useState<SurveyMasterSearchType>({
		title: '',
		startDate: '',
		endDate: '',
	})

	// 추가
	const actionAdd = () => {}

	// 삭제
	const actionDel = () => {}

	// 저장
	const actionSave = () => {}

	// 검색
	const actionSearch = () => {
		setLoading(true)
		apiSurveyMasterList(search)
			.then((res) => {
				console.log(res)
				setRows(res)
			})
			.finally(() => {
				setLoading(false)
			})
	}

	// 초기화
	const actionReset = () => {
		// 검색 폼 초기화
		setSearch({
			title: '',
			startDate: '',
			endDate: '',
		})
		// 선택 초기화
		setRowSelectionModel({ type: 'include', ids: new Set() })
		// 데이터 다시 조회
		actionSearch()
	}

	// 키보드 엔터키 처리
	const onKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
		if (e.code === 'Enter' || e.key === 'Enter') {
			e.preventDefault() // 폼 제출 방지
			actionSearch()
		}
	}

	// 폼 제출 방지
	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
	}

	useEffect(() => {
		actionSearch()
	}, [])

	return (
		<PageContainer>
			<SurveyAnswersPopup
				isOpen={popupIsOpen}
				onClose={() => setPopupIsOpen(false)}
				data={selectedData}
				subData={surveyAnswers}
			/>
			<PageTop>
				<PagePosition
					actionAdd={actionAdd}
					actionDel={actionDel}
					actionSave={actionSave}
					actionSearch={actionSearch}
					actionReset={actionReset}
					actionExcelDownload={() => {}}
					disabledAdd={disabledAdd}
					disabledDel={disabledDel}
					disabledSave={disabledSave}
					disabledSearch={disabledSearch}
					disabledReset={disabledReset}
					disabledExcelDownload={disabledExcelDownload}
					hideAdd={hideAdd}
					hideDel={hideDel}
					hideSave={hideSave}
					hideSearch={hideSearch}
					hideReset={hideReset}
					hideExcelDownload={hideExcelDownload}
				/>
				<SearchContainer
					onKeyDown={onKeyDown}
					onSubmit={onSubmit}
				>
					<Grid
						container
						spacing={2}
						// justifyContent={'flex-end'}
					>
						<Grid size={12}>
							<TextField
								label="설문 제목"
								variant="outlined"
								fullWidth
								value={search.title}
								onChange={(e) => setSearch({ ...search, title: e.target.value })}
							/>
						</Grid>
					</Grid>
				</SearchContainer>
			</PageTop>
			<GridContainer>
				<StyledDataGrid
					apiRef={apiRef}
					columns={gridColumns}
					rows={rows}
					showToolbar
					loading={loading}
					localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
					rowSelectionModel={rowSelectionModel}
					onRowSelectionModelChange={(newRowSelectionModel) => {
						setRowSelectionModel(newRowSelectionModel)
					}}
					disableMultipleRowSelection
					initialState={{
						columns: {
							columnVisibilityModel: {
								crud: false,
								id: false,
							},
						},
					}}
				/>
			</GridContainer>
		</PageContainer>
	)
}

export default SurveyResult
